import { Theme } from "../types.ts";
import { fontDefinitions } from "../utils/helpers.ts";
import { App } from "../ui/App.ts";

export class PosterRenderer {
  private app: App;

  constructor(app: App) {
    this.app = app;
  }

  renderPoster(mapData: any, city: string, country: string, location: any) {
    const theme = this.app.themeManager.currentTheme;
    if (!theme) return;

    let width: number, height: number;
    if (this.app.printFormat === "square") {
      width = 3000;
      height = 3000;
    } else {
      width = 2480;
      height = 3508;
    }

    this.app.canvas.width = width;
    this.app.canvas.height = height;

    this.app.ctx.fillStyle = theme.bg;
    this.app.ctx.fillRect(0, 0, width, height);

    const margin = 200;
    const textReserve = this.app.printFormat === "square" ? 300 : 400;
    const availableWidth = width - 2 * margin;
    const availableHeight = height - 2 * margin - textReserve;

    const centerLat = location.lat;
    const centerLon = location.lon;
    const distKm = location.radius || 5;

    // Scale: Pixels per km
    // We want physical distance to be uniform, so we calculate pixels per km first
    // and then convert to pixels per degree based on the center point.
    const pixelsPerKm = Math.min(availableWidth / (2 * distKm), availableHeight / (2 * distKm));

    const scaleX = pixelsPerKm * (111.32 * Math.cos(centerLat * Math.PI / 180));
    const scaleY = pixelsPerKm * 111.32;

    const centerX = margin + availableWidth / 2;
    const centerY = margin + availableHeight / 2;

    const project = (pLat: number, pLon: number) => {
      const x = centerX + (pLon - centerLon) * scaleX;
      const y = centerY - (pLat - centerLat) * scaleY;
      return [x, y];
    };

    if (mapData.water && theme.water) {
      this.app.ctx.fillStyle = theme.water;
      mapData.water.forEach((feature: any) => {
        feature.geometry.coordinates.forEach((polygon: any) => {
          this.app.ctx.beginPath();
          polygon.forEach(([lon, lat]: [number, number], i: number) => {
            const [x, y] = project(lat, lon);
            if (i === 0) {
              this.app.ctx.moveTo(x, y);
            } else {
              this.app.ctx.lineTo(x, y);
            }
          });
          this.app.ctx.closePath();
          this.app.ctx.fill();
        });
      });
    }

    if (mapData.parks && theme.parks) {
      this.app.ctx.fillStyle = theme.parks;
      mapData.parks.forEach((feature: any) => {
        feature.geometry.coordinates.forEach((polygon: any) => {
          this.app.ctx.beginPath();
          polygon.forEach(([lon, lat]: [number, number], i: number) => {
            const [x, y] = project(lat, lon);
            if (i === 0) {
              this.app.ctx.moveTo(x, y);
            } else {
              this.app.ctx.lineTo(x, y);
            }
          });
          this.app.ctx.closePath();
          this.app.ctx.fill();
        });
      });
    }

    if (mapData.buildings && (theme.buildings || true)) {
      this.app.ctx.fillStyle = theme.buildings || "rgba(128, 128, 128, 0.15)";
      mapData.buildings.forEach((feature: any) => {
        feature.geometry.coordinates.forEach((polygon: any) => {
          this.app.ctx.beginPath();
          polygon.forEach(([lon, lat]: [number, number], i: number) => {
            const [x, y] = project(lat, lon);
            if (i === 0) {
              this.app.ctx.moveTo(x, y);
            } else {
              this.app.ctx.lineTo(x, y);
            }
          });
          this.app.ctx.closePath();
          this.app.ctx.fill();
        });
      });
    }

    const nodeMap = new Map<any, any>(mapData.nodes.map((n: any) => [n.id, n]));

    mapData.edges.forEach((edge: any) => {
      const source = nodeMap.get(edge.source);
      const target = nodeMap.get(edge.target);

      if (!source || !target) return;

      const highway = edge.highway || "default";
      let color = theme.road_default || theme.text;
      let strokeWidth = 0.5;

      if (highway.includes("motorway")) {
        color = theme.road_motorway || theme.text;
        strokeWidth = 3;
      } else if (highway.includes("primary")) {
        color = theme.road_primary || theme.text;
        strokeWidth = 2.5;
      } else if (highway.includes("secondary")) {
        color = theme.road_secondary || theme.text;
        strokeWidth = 2;
      } else if (highway.includes("tertiary")) {
        color = theme.road_tertiary || theme.text;
        strokeWidth = 1.5;
      } else if (highway.includes("residential") || highway.includes("living_street")) {
        color = theme.road_residential || theme.text;
        strokeWidth = 1;
      }

      this.app.ctx.strokeStyle = color;
      this.app.ctx.lineWidth = strokeWidth;
      this.app.ctx.lineCap = "round";

      this.app.ctx.beginPath();
      const [x1, y1] = project(source.lat, source.lon);
      const [x2, y2] = project(target.lat, target.lon);
      this.app.ctx.moveTo(x1, y1);
      this.app.ctx.lineTo(x2, y2);
      this.app.ctx.stroke();
    });

    if (mapData.railways) {
      this.app.ctx.strokeStyle = theme.road_default || theme.text;
      this.app.ctx.lineWidth = 0.5;
      this.app.ctx.lineCap = "round";
      this.app.ctx.setLineDash([2, 4]);
      this.app.ctx.globalAlpha = 0.4; // make it subtle

      mapData.railways.forEach((edge: any) => {
        const source = nodeMap.get(edge.source);
        const target = nodeMap.get(edge.target);
        if (!source || !target) return;
        this.app.ctx.beginPath();
        const [x1, y1] = project(source.lat, source.lon);
        const [x2, y2] = project(target.lat, target.lon);
        this.app.ctx.moveTo(x1, y1);
        this.app.ctx.lineTo(x2, y2);
        this.app.ctx.stroke();
      });
      this.app.ctx.setLineDash([]); // Reset line dash
      this.app.ctx.globalAlpha = 1.0; // Reset alpha
    }

    this.app.ctx.fillStyle = theme.text;
    this.app.ctx.textAlign = "center";

    const fontFamily = fontDefinitions[this.app.advancedSettings.fontFamily] ||
      fontDefinitions["Geometric Humanist"];
    const displayCity = this.app.advancedSettings.customCity || city;
    const displayCountry = this.app.advancedSettings.customCountry || country;

    const displayLat = this.app.advancedSettings.customLat
      ? parseFloat(this.app.advancedSettings.customLat)
      : location.lat;
    const displayLon = this.app.advancedSettings.customLon
      ? parseFloat(this.app.advancedSettings.customLon)
      : location.lon;

    this.app.ctx.font = `bold 120px ${fontFamily}`;
    this.app.ctx.fillText(displayCity.toUpperCase().split("").join(" "), width / 2, height - 450);

    this.app.ctx.fillRect(width / 2 - 200, height - 400, 400, 2);

    this.app.ctx.font = `60px ${fontFamily}`;
    this.app.ctx.fillText(displayCountry.toUpperCase(), width / 2, height - 320);

    this.app.ctx.font = `40px ${fontFamily}`;
    const latValue = isNaN(displayLat) ? location.lat : displayLat;
    const lonValue = isNaN(displayLon) ? location.lon : displayLon;
    const coordsText = `${Math.abs(latValue).toFixed(4)}° ${latValue >= 0 ? "N" : "S"}, ${
      Math.abs(lonValue).toFixed(4)
    }° ${lonValue >= 0 ? "E" : "W"}`;
    this.app.ctx.fillText(coordsText, width / 2, height - 250);

    this.app.ctx.font = `30px ${fontFamily}`;
    this.app.ctx.textAlign = "right";
    this.app.ctx.fillText("© OpenStreetMap contributors", width - 100, height - 80);
  }

  downloadPoster(format = "png") {
    const cityInput = document.getElementById("city") as HTMLInputElement;
    const city = cityInput?.value || "map";
    const theme = this.app.themeManager.currentTheme?.name || "default";
    const timestamp = new Date().toISOString().split("T")[0];
    const safeCity = city.toLowerCase().replace(/[^a-z0-9]/g, "_");
    const safeTheme = theme.toLowerCase().replace(/[^a-z0-9]/g, "_");

    if (format === "pdf") {
      this.downloadPosterAsPdf(safeCity, safeTheme, timestamp);
    } else if (format === "svg") {
      this.downloadPosterAsSvg(safeCity, safeTheme, timestamp);
    } else {
      this.downloadPosterAsPng(safeCity, safeTheme, timestamp);
    }
  }

  generateSvgString(overrides: any = {}): string | null {
    const mapData = overrides.mapData || this.app.cache.get().data;
    if (!mapData) {
      alert("No map data available to generate SVG");
      return null;
    }

    const cityInput = document.getElementById("city") as HTMLInputElement;
    const countryInput = document.getElementById("country") as HTMLInputElement;

    const city = overrides.city || cityInput?.value;
    const country = overrides.country || countryInput?.value;
    const location = overrides.location || this.app.cache.get().location;
    const theme = overrides.theme || this.app.themeManager.currentTheme;
    const printFormat = overrides.printFormat || this.app.printFormat;

    let width: number, height: number;
    if (printFormat === "square") {
      width = 3000;
      height = 3000;
    } else {
      width = 2480;
      height = 3508;
    }

    const margin = 200;
    const textReserve = printFormat === "square" ? 300 : 400;
    const availableWidth = width - 2 * margin;
    const availableHeight = height - 2 * margin - textReserve;

    const centerLat = location.lat;
    const centerLon = location.lon;
    const distKm = location.radius || 5;

    const pixelsPerKm = Math.min(availableWidth / (2 * distKm), availableHeight / (2 * distKm));
    const scaleX = pixelsPerKm * (111.32 * Math.cos(centerLat * Math.PI / 180));
    const scaleY = pixelsPerKm * 111.32;

    const centerX = margin + availableWidth / 2;
    const centerY = margin + availableHeight / 2;

    const project = (pLat: number, pLon: number) => {
      const x = centerX + (pLon - centerLon) * scaleX;
      const y = centerY - (pLat - centerLat) * scaleY;
      return [x, y];
    };

    let svg = `<?xml version="1.0" encoding="UTF-8" standalone="no"?>
<svg width="${width}" height="${height}" viewBox="0 0 ${width} ${height}" xmlns="http://www.w3.org/2000/svg">
  <rect width="100%" height="100%" fill="${theme.bg}" />
  <g id="water">`;

    if (mapData.water && theme.water) {
      mapData.water.forEach((feature: any) => {
        feature.geometry.coordinates.forEach((polygon: any) => {
          let path = "M ";
          polygon.forEach(([lon, lat]: [number, number]) => {
            const [x, y] = project(lat, lon);
            path += `${x.toFixed(2)},${y.toFixed(2)} `;
          });
          svg += `\\n    <path d="${path.trim()} Z" fill="${theme.water}" />`;
        });
      });
    }
    svg += '\\n  </g>\\n  <g id="parks">';

    if (mapData.parks && theme.parks) {
      mapData.parks.forEach((feature: any) => {
        feature.geometry.coordinates.forEach((polygon: any) => {
          let path = "M ";
          polygon.forEach(([lon, lat]: [number, number]) => {
            const [x, y] = project(lat, lon);
            path += `${x.toFixed(2)},${y.toFixed(2)} `;
          });
          svg += `\\n    <path d="${path.trim()} Z" fill="${theme.parks}" />`;
        });
      });
    }
    svg += '\\n  </g>\\n  <g id="buildings">';

    if (mapData.buildings && (theme.buildings || true)) {
      mapData.buildings.forEach((feature: any) => {
        feature.geometry.coordinates.forEach((polygon: any) => {
          let path = "M ";
          polygon.forEach(([lon, lat]: [number, number]) => {
            const [x, y] = project(lat, lon);
            path += `${x.toFixed(2)},${y.toFixed(2)} `;
          });
          const fill = theme.buildings || "rgba(128, 128, 128, 0.15)";
          svg += `\\n    <path d="${path.trim()} Z" fill="${fill}" />`;
        });
      });
    }
    svg += '\\n  </g>\\n  <g id="roads">';

    const nodeMap = new Map<any, any>(mapData.nodes.map((n: any) => [n.id, n]));
    mapData.edges.forEach((edge: any) => {
      const source = nodeMap.get(edge.source);
      const target = nodeMap.get(edge.target);
      if (!source || !target) return;

      const highway = edge.highway || "default";
      let strokeColor = theme.road_default || theme.text;
      let strokeWidth = 0.5;

      if (highway.includes("motorway")) {
        strokeColor = theme.road_motorway || theme.text;
        strokeWidth = 3;
      } else if (highway.includes("primary")) {
        strokeColor = theme.road_primary || theme.text;
        strokeWidth = 2.5;
      } else if (highway.includes("secondary")) {
        strokeColor = theme.road_secondary || theme.text;
        strokeWidth = 2;
      } else if (highway.includes("tertiary")) {
        strokeColor = theme.road_tertiary || theme.text;
        strokeWidth = 1.5;
      } else if (highway.includes("residential")) {
        strokeColor = theme.road_residential || theme.text;
        strokeWidth = 1;
      }

      const [x1, y1] = project(source.lat, source.lon);
      const [x2, y2] = project(target.lat, target.lon);
      svg += `\\n    <line x1="${x1.toFixed(2)}" y1="${y1.toFixed(2)}" x2="${x2.toFixed(2)}" y2="${
        y2.toFixed(2)
      }" stroke="${strokeColor}" stroke-width="${strokeWidth}" stroke-linecap="round" />`;
    });
    svg += '\\n  </g>\\n  <g id="railways">';

    if (mapData.railways) {
      mapData.railways.forEach((edge: any) => {
        const source = nodeMap.get(edge.source);
        const target = nodeMap.get(edge.target);
        if (!source || !target) return;

        const [x1, y1] = project(source.lat, source.lon);
        const [x2, y2] = project(target.lat, target.lon);
        const strokeColor = theme.road_default || theme.text;
        svg += `\\n    <line x1="${x1.toFixed(2)}" y1="${y1.toFixed(2)}" x2="${
          x2.toFixed(2)
        }" y2="${
          y2.toFixed(2)
        }" stroke="${strokeColor}" stroke-opacity="0.4" stroke-width="0.5" stroke-linecap="round" stroke-dasharray="2,4" />`;
      });
    }

    svg += '\\n  </g>\\n  <g id="text">';

    const fontFamily = fontDefinitions[this.app.advancedSettings.fontFamily] || "Arial, sans-serif";
    const displayCity = this.app.advancedSettings.customCity || city;
    const displayCountry = this.app.advancedSettings.customCountry || country;
    const displayLat = this.app.advancedSettings.customLat
      ? parseFloat(this.app.advancedSettings.customLat)
      : location.lat;
    const displayLon = this.app.advancedSettings.customLon
      ? parseFloat(this.app.advancedSettings.customLon)
      : location.lon;

    svg += `\\n    <text x="${width / 2}" y="${
      height - 450
    }" text-anchor="middle" font-family="${fontFamily}" font-size="120" font-weight="bold" fill="${theme.text}" style="letter-spacing: 0.5em;">${displayCity.toUpperCase()}</text>`;
    svg += `\\n    <rect x="${width / 2 - 200}" y="${
      height - 400
    }" width="400" height="2" fill="${theme.text}" />`;
    svg += `\\n    <text x="${width / 2}" y="${
      height - 320
    }" text-anchor="middle" font-family="${fontFamily}" font-size="60" fill="${theme.text}">${displayCountry.toUpperCase()}</text>`;

    const latDir = displayLat >= 0 ? "N" : "S";
    const lonDir = displayLon >= 0 ? "E" : "W";
    const coordsText = `${Math.abs(displayLat).toFixed(4)}° ${latDir} / ${
      Math.abs(displayLon).toFixed(4)
    }° ${lonDir}`;
    svg += `\\n    <text x="${width / 2}" y="${
      height - 240
    }" text-anchor="middle" font-family="${fontFamily}" font-size="40" fill="${theme.text}" font-weight="500">${coordsText}</text>`;

    svg += "\n  </g>\n</svg>";

    return svg;
  }

  downloadPosterAsSvg(safeCity: string, safeTheme: string, timestamp: string, overrides: any = {}) {
    const svg = this.generateSvgString(overrides);
    if (!svg) return;

    const blob = new Blob([svg], { type: "image/svg+xml" });
    const url = URL.createObjectURL(blob);
    const filename = `${safeCity}_${safeTheme}_${timestamp}.svg`;

    const link = document.createElement("a");
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  }

  downloadPosterAsPng(safeCity: string, safeTheme: string, timestamp: string) {
    const filename = `${safeCity}_${safeTheme}_${timestamp}.png`;

    this.app.canvas.toBlob((blob) => {
      if (!blob) {
        alert("Failed to create image");
        return;
      }
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      setTimeout(() => URL.revokeObjectURL(url), 100);
    }, "image/png");
  }

  async downloadPosterAsPdf(safeCity: string, safeTheme: string, timestamp: string) {
    const filename = `${safeCity}_${safeTheme}_${timestamp}.pdf`;

    if (!(globalThis as any).jspdf) {
      await this.app.loadScript(
        "https://cdnjs.cloudflare.com/ajax/libs/jspdf/2.5.1/jspdf.umd.min.js",
      );
    }

    const { jsPDF } = (globalThis as any).jspdf;

    const canvasWidth = this.app.canvas.width;
    const canvasHeight = this.app.canvas.height;
    const isPortrait = canvasHeight > canvasWidth;
    const orientation = isPortrait ? "portrait" : "landscape";

    const pdf = new jsPDF({
      orientation: orientation,
      unit: "mm",
      format: "a4",
    });

    const pageWidth = pdf.internal.pageSize.getWidth();
    const pageHeight = pdf.internal.pageSize.getHeight();

    const canvasRatio = canvasWidth / canvasHeight;
    const pageRatio = pageWidth / pageHeight;

    let imgWidth, imgHeight;
    if (canvasRatio > pageRatio) {
      imgWidth = pageWidth;
      imgHeight = pageWidth / canvasRatio;
    } else {
      imgHeight = pageHeight;
      imgWidth = pageHeight * canvasRatio;
    }

    const xOffset = (pageWidth - imgWidth) / 2;
    const yOffset = (pageHeight - imgHeight) / 2;

    const imgData = this.app.canvas.toDataURL("image/png", 1.0);
    pdf.addImage(imgData, "PNG", xOffset, yOffset, imgWidth, imgHeight);

    pdf.save(filename);
  }
}
